import { ref } from 'vue'

import { closeModal, openModal } from '@ds/components/ModalBase'

import type { ChatRequestPageContext } from '~/api/shopping-assistant/types'

import { useShoppingAssistantWebSidebar } from '../stores/useShoppingAssistantWebSidebar'

import type { EntryPoint } from './useShoppingAssistantContext'

export interface AssistantEntryPayload {
  message: string
  pageContext: ChatRequestPageContext | null
  // Opaque payload echoed back to onLaunch (e.g. the clicked pill id).
  meta: Record<string, unknown>
}

/**
 * Launches the web sidebar assistant from an in-page entry point (the search
 * bar's "search with AI", the PDP suggestion pills), orchestrating the
 * open-or-confirm-then-restart flow around `useShoppingAssistantWebSidebar`:
 *
 * - sidebar closed → open() a brand-new conversation seeded with the message
 *   and page context;
 * - sidebar open and resuming → inject the message into the running chat via
 *   store.setPendingMessage (no remount);
 * - sidebar open with an empty conversation and no resume → restart() it
 *   (nothing to lose);
 * - sidebar open with a real conversation and no resume → ask for
 *   confirmation first, then restart() on accept and leave the conversation
 *   untouched on decline.
 *
 * The confirmation dialog is supported but currently unused — no active entry
 * point passes a `dialogName`. The flow is kept for future entry points that
 * need destructive-action confirmation.
 *
 * `onLaunch` fires whenever a conversation is actually opened or restarted —
 * immediately, or after the dialog is accepted — and never on decline, so it
 * is the right seam for entry tracking.
 */
export function useShoppingAssistantEntry({
  dialogName,
  entryPoint,
  shouldResumeLatest = true,
  onLaunch,
}: {
  // If not provided, user will not be prompt when creating a new discussion
  dialogName?: string
  entryPoint: EntryPoint
  // When true, the next mount resumes the user's latest server-side chat
  // instead of creating a new one. Entry points that "continue the
  // conversation" (pills) set this; entry points that start fresh do not.
  shouldResumeLatest?: boolean
  onLaunch?: (payload: AssistantEntryPayload) => void
}) {
  const store = useShoppingAssistantWebSidebar()

  const pendingPayload = ref<AssistantEntryPayload | null>(null)

  function launch({
    message = '',
    pageContext = null,
    meta = {},
  }: {
    message?: string
    pageContext?: ChatRequestPageContext | null
    meta?: Record<string, unknown>
  } = {}): void {
    const payload: AssistantEntryPayload = { message, pageContext, meta }

    if (!store.isOpen) {
      store.open({
        entryPoint,
        message,
        pageContext,
        shouldResumeLatest,
      })
      onLaunch?.(payload)

      return
    }

    // When resuming and the sidebar is already open, inject the message
    // into the running chat without remounting — the host watches
    // store.pendingMessage and forwards it to the chat's receivedEvent.
    // When the message is empty (e.g. the "Ask something else" CTA), the
    // input is already visible — nothing to inject, so skip the tracking
    // event to keep analytics truthful.
    if (shouldResumeLatest) {
      if (message) {
        store.setPendingMessage(message)
        onLaunch?.(payload)
      }

      return
    }

    // Confirm only when there's a real conversation to lose — same signal
    // the web-sidebar host adapter uses (see useWebSidebarShoppingAssistantHost's
    // `newChat`), so an open-but-empty panel is replaced directly instead of
    // prompting for nothing.
    if (dialogName && store.hasUserMessages) {
      pendingPayload.value = payload
      openModal(dialogName)

      return
    }

    store.restart({
      entryPoint,
      message,
      pageContext,
      shouldResumeLatest,
    })
    onLaunch?.(payload)
  }

  function accept(): void {
    closeModal(dialogName)

    const payload = pendingPayload.value
    pendingPayload.value = null

    if (!payload) return

    store.restart({
      entryPoint,
      message: payload.message,
      pageContext: payload.pageContext,
      shouldResumeLatest,
    })
    onLaunch?.(payload)
  }

  function decline(): void {
    closeModal(dialogName)
    pendingPayload.value = null
  }

  return {
    launch,
    accept,
    decline,
  }
}
